import { db } from '../config/firebase';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
// ** Import only exported members **
import { parseNumericValue, formatDateMMDDYYYY } from './pmt2Utils'; // Import helpers
import { sanitizeDocIdSync, validateFirestorePath } from '../utils/pmt2PathUtils';

// Collection name
const LEDGER_COLLECTION_BASE = 'pmt2_ledger';

/**
 * Fetches entries from a specific document within the ledger structure,
 * assuming entries are stored in an 'entries' array field.
 * Sorts entries by date ascending.
 */
const getLedgerEntries = async (companyName: string, asin: string, dataElementName: string): Promise<any[]> => {
    // ... (keep existing implementation)
    if (!companyName || !asin || !dataElementName) {
        return [];
    }
    try {
        // Sanitize path components to prevent path length errors
        const sanitizedAsin = sanitizeDocIdSync(asin, {
            maxLength: 100,
            context: 'ledger-asin'
        });
        const sanitizedDataElement = sanitizeDocIdSync(dataElementName, {
            maxLength: 100,
            context: 'ledger-dataElement'
        });
        
        // Validate the complete path
        const fullPath = `${LEDGER_COLLECTION_BASE}/${companyName}/${sanitizedAsin}/${sanitizedDataElement}`;
        const pathValidation = validateFirestorePath(fullPath);
        
        if (!pathValidation.valid) {
            console.warn(`[LedgerService] Path validation failed for ${dataElementName}: ${pathValidation.message}`);
        }
        
        const docRef = doc(db, LEDGER_COLLECTION_BASE, companyName, sanitizedAsin, sanitizedDataElement);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            if (Array.isArray(data?.entries)) {
                return data.entries
                    .filter(e => e.date?.toDate) // Ensure date exists and is convertible
                    .sort((a, b) => (a.date.toDate().getTime() || 0) - (b.date.toDate().getTime() || 0)); // Sort ascending
            }
            return [];
        } else {
            return [];
        }
    } catch (error) {
        throw error; // Re-throw error
    }
};

// --- Other Ledger Fetch Functions ---
export const getASINReviewHistory = (companyName: string, asin: string): Promise<any[]> => getLedgerEntries(companyName, asin, 'Standard ASIN Review');
export const getPPCSituationHistory = (companyName: string, asin: string): Promise<any[]> => getLedgerEntries(companyName, asin, 'Overall PPC Situation');
export const getPPCStatusHistory = (companyName: string, asin: string): Promise<any[]> => getLedgerEntries(companyName, asin, 'PPC Status');
export const getPriceHistory = (companyName: string, asin: string): Promise<any[]> => getLedgerEntries(companyName, asin, 'Price');
export const getClientNoteForAsin = async (companyName: string, asin: string): Promise<any | null> => {
     if (!companyName || !asin) return null;
    try {
        // Sanitize path components
        const sanitizedAsin = sanitizeDocIdSync(asin, {
            maxLength: 100,
            context: 'ledger-client-note-asin'
        });
        const sanitizedDataElement = sanitizeDocIdSync('Client Note', {
            maxLength: 100,
            context: 'ledger-client-note-element'
        });
        
        // Validate the complete path
        const fullPath = `${LEDGER_COLLECTION_BASE}/${companyName}/${sanitizedAsin}/${sanitizedDataElement}`;
        const pathValidation = validateFirestorePath(fullPath);
        
        if (!pathValidation.valid) {
            console.warn(`[LedgerService] Path validation failed for Client Note: ${pathValidation.message}`);
        }
        
        const docRef = doc(db, LEDGER_COLLECTION_BASE, companyName, sanitizedAsin, sanitizedDataElement);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (error) {
        throw error;
    }
};
// --- End Other Ledger Fetch Functions ---

// Helper to format value for change details string
const formatChangeValue = (elementName: string, value: number | null): string => {
    if (value === null) return 'cleared';
    // ** Use imported parseNumericValue **
    const numValue = parseNumericValue(value); // Use the correct imported function
    if (numValue === null) return 'invalid'; // Should ideally not happen if parsing worked before

    if (elementName.includes('%')) return `${Math.round(numValue)}%`; // Assumes value is already correct percentage number
    if (['Price', 'COGS', 'FBA Fee', 'Gross Profit Override'].includes(elementName)) return `$${numValue.toFixed(2)}`;
    return String(numValue);
};


/**
 * Fetches ASIN-specific margin history data using state reconstruction.
 * Includes the 'changedFields' array needed for frontend highlighting.
 * @param companyName The company name
 * @param asin The ASIN to fetch margin history for
 * @returns Array of merged margin history entries, sorted newest first.
 */
export const getASINMarginHistory = async (companyName: string, asin: string): Promise<any[]> => {
    if (!companyName || !asin) return [];

    try {
        const dataElementNames = [
            'Price', 'COGS', 'FBA Fee', 'Referral Fee %',
            'Gross Profit Override', 'Target ACoS Override'
        ];
        const allEntries: { [key: string]: any[] } = {};
        const changesByTimestamp = new Map<number, { [key: string]: number | null }>();
        const uniqueTimestamps = new Set<number>();

        // 1. Fetch all entries & Pre-process changes into changesByTimestamp map
        for (const elementName of dataElementNames) {
            allEntries[elementName] = await getLedgerEntries(companyName, asin, elementName);
            for (const entry of allEntries[elementName]) {
                if (!entry.date?.toDate) continue;
                const timestampMs = entry.date.toDate().getTime();
                uniqueTimestamps.add(timestampMs);

                const newValueRaw = entry['New Value'];
                let parsedValue: number | null = null;

                if ((elementName === 'Gross Profit Override' || elementName === 'Target ACoS Override') && newValueRaw === 'default') {
                    parsedValue = null;
                } else {
                    // ** Use imported parseNumericValue **
                    parsedValue = parseNumericValue(newValueRaw); // Handles $, %, ,
                }

                // Store the parsed value associated with its exact timestamp
                if (!changesByTimestamp.has(timestampMs)) {
                    changesByTimestamp.set(timestampMs, {});
                }
                // Ensure the value is stored correctly, even if null
                changesByTimestamp.get(timestampMs)![elementName] = parsedValue;
            }
        }

        // Fetch latest buffer override separately
        let grossProfitBufferPercent = 0.03; // Default
        try {
            const bufferEntries = await getLedgerEntries(companyName, asin, 'Gross Profit Buffer % Override');
            if (bufferEntries.length > 0) {
                const latestValue = parseNumericValue(bufferEntries[bufferEntries.length - 1]?.['New Value']);
                if (latestValue !== null) {
                    grossProfitBufferPercent = latestValue / 100; // Assume stored as number e.g. 3 for 3%
                }
            }
        } catch (error) { }

        if (uniqueTimestamps.size === 0) return [];

        // 2. Sort unique timestamps oldest to newest
        const sortedTimestamps = Array.from(uniqueTimestamps).sort((a, b) => a - b);

        // 3. Iterate & Reconstruct State
        const results: any[] = [];
        let previousState: { [key: string]: any } = dataElementNames.reduce((acc, name) => {
            acc[name] = null; // Initialize all state fields to null
            return acc;
        }, {} as { [key: string]: any });

        for (const timestampMs of sortedTimestamps) {
            // 4a. Start with previous state
            let currentState = { ...previousState };
            let changeOccurred = false;
            const changedFieldsDetails: string[] = []; // For popup details string
            const changedFieldsArray: string[] = []; // ** Initialize array for field names **

            // 4b. Merge in specific changes for this timestamp
            const actualChanges = changesByTimestamp.get(timestampMs) || {};
            for (const elementName in actualChanges) {
                 // Ensure we only process expected elements
                 if (dataElementNames.includes(elementName)) {
                    const newValue = actualChanges[elementName];
                    // Check if this change is different from the previous state
                    if (newValue !== previousState[elementName]) {
                        changeOccurred = true;
                        changedFieldsDetails.push(`${elementName}: ${formatChangeValue(elementName, newValue)}`);
                        // ** Add the field name to the array **
                        changedFieldsArray.push(elementName);
                    }
                    // Update the current state with the value from this timestamp
                    currentState[elementName] = newValue;
                 }
            }

            // 4c. Calculate derived values based on the fully reconstructed currentState
            let finalGrossProfitDollars: number | null = null;
            const gpOverride = currentState['Gross Profit Override'];
            const price = currentState['Price'];
            const refFee = currentState['Referral Fee %']; // Assumes stored as decimal e.g. 0.15
            const cogs = currentState['COGS'];
            const fbaFee = currentState['FBA Fee'];

            if (gpOverride !== null && gpOverride !== 0) {
                finalGrossProfitDollars = gpOverride;
            } else if (price !== null && refFee !== null && cogs !== null && fbaFee !== null) {
                finalGrossProfitDollars = price * (1 - (refFee + grossProfitBufferPercent)) - cogs - fbaFee;
                finalGrossProfitDollars = Math.round(finalGrossProfitDollars * 100) / 100;
            }

            let finalTargetAcos: number | null = null;
            const targetOverride = currentState['Target ACoS Override']; // Assumes stored as number e.g. 20 for 20%
            if (targetOverride !== null && targetOverride !== 0) {
                finalTargetAcos = targetOverride;
            } else if (finalGrossProfitDollars !== null && price !== null && price !== 0) {
                finalTargetAcos = Math.round((finalGrossProfitDollars / price) * 100);
            }

            // 4e. Create the result entry for this timestamp
            const resultEntry: any = {
                id: `${asin}_${timestampMs}`,
                date: Timestamp.fromMillis(timestampMs),
                ...currentState, // Include all current state fields
                'Final Gross Profit $': finalGrossProfitDollars,
                'Final Target ACoS': finalTargetAcos,
                'Gross Profit Buffer %': grossProfitBufferPercent, // Include buffer used
                change: changeOccurred ? 'Change' : '',
                changeDetails: changedFieldsDetails.join(', '), // Use formatted details
                // ** Include the changedFields array **
                changedFields: changedFieldsArray
            };

            results.push(resultEntry);

            // 4f. Update previousState for the next iteration
            previousState = currentState;
        }

        // 5. Sort final results newest first
        return results.sort((a, b) => b.date.toDate().getTime() - a.date.toDate().getTime());

    } catch (error) {
        throw error;
    }
};